/**
 * SAFE FIRESTORE WRAPPERS
 * 
 * Wrappers for Firestore operations that prevent "Uncaught Error" from crashing the app.
 * These wrappers catch ALL possible errors, including async errors that bypass normal error handlers.
 */

import { 
  DocumentReference, 
  Query, 
  onSnapshot, 
  Unsubscribe,
  DocumentSnapshot,
  QuerySnapshot
} from 'firebase/firestore';

/**
 * Safe wrapper for onSnapshot that prevents uncaught errors
 * This is the ONLY way to call onSnapshot in this app
 */
export const safeOnSnapshot = (
  ref: DocumentReference | Query,
  onNext: (snapshot: DocumentSnapshot | QuerySnapshot) => void,
  onError?: (error: any) => void
): Unsubscribe => {
  // CRITICAL: Wrap the entire onSnapshot call to catch ANY error
  let unsubscribe: Unsubscribe | null = null;
  
  try {
    unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        // CRITICAL: Wrap callback in try-catch
        try {
          onNext(snapshot);
        } catch (callbackError: any) {
          // Never let callback errors propagate
          if (import.meta.env.DEV) {
            console.warn('[SAFE_FIRESTORE] Error in snapshot callback:', callbackError);
          }
          // Call error handler if provided
          if (onError) {
            try {
              onError(callbackError);
            } catch {
              // Even error handler must not throw
            }
          }
        }
      },
      (error) => {
        // CRITICAL: Handle ALL Firestore errors
        try {
          if (onError) {
            onError(error);
          } else {
            // Default: silently handle permission-denied
            if (import.meta.env.DEV && error?.code !== 'permission-denied') {
              console.warn('[SAFE_FIRESTORE] Unhandled Firestore error:', error);
            }
          }
        } catch (errorHandlerError: any) {
          // Even error handler must not throw
          if (import.meta.env.DEV) {
            console.error('[SAFE_FIRESTORE] Error in error handler (critical):', errorHandlerError);
          }
        }
      }
    );
  } catch (initError: any) {
    // CRITICAL: Catch initialization errors
    if (import.meta.env.DEV) {
      console.error('[SAFE_FIRESTORE] Error initializing snapshot:', initError);
    }
    // Call error handler if provided
    if (onError) {
      try {
        onError(initError);
      } catch {
        // Silently ignore
      }
    }
    // Return a no-op unsubscribe function
    return () => {};
  }

  // Return safe unsubscribe function
  return () => {
    if (typeof unsubscribe === 'function') {
      try {
        unsubscribe();
      } catch {
        // Silently ignore cleanup errors
      }
    }
  };
};

